import React, { useState, useEffect, useMemo } from "react";
import { Table, Row, Col, Modal, Spinner } from "react-bootstrap";
import Footer from "../layouts/Footer";
import PaginationControl from "../components/PaginationControl";
import { fetchGsmSmsHistory, fetchGsmClients } from "../services/api";
import { GsmStyles, GsmToasts, useNotifications } from "./GsmShared";

const EMPTY_FILTERS = {
  business_id: '',
  date_from: '',
  date_to: '',
  type_sms: '',
  num_en_ref: ''
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const TYPE_BADGE = {
  MPayment: 'gsm-badge-green',
  Cashin: 'gsm-badge-green',
  Cashout: 'gsm-badge-red',
  Dealer: 'gsm-badge-amber',
  Airtime: 'gsm-badge-gray',
  Data: 'gsm-badge-gray',
  Promo: 'gsm-badge-gray'
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  if (isNaN(d)) return value;
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  return isNaN(n) ? value : `${n.toLocaleString('fr-FR', { maximumFractionDigits: 3 })} F`;
};

const statusBadge = (status) => {
  const code = Number(status);
  if (code === 200) return <span className="gsm-badge gsm-badge-green">200</span>;
  if (code === 201) return <span className="gsm-badge gsm-badge-amber">201</span>;
  if (!status) return <span className="text-muted">—</span>;
  return <span className="gsm-badge gsm-badge-red">{status}</span>;
};

export default function GsmHistoriqueSms() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 25, total: 0 });
  const [types, setTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtres appliqués (envoyés à l'API) et brouillon édité dans le modal
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [selectedSms, setSelectedSms] = useState(null);

  const { notifications, showNotification, hideNotification } = useNotifications();

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetchGsmClients(token)
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    loadSms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, perPage]);

  const loadSms = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const result = await fetchGsmSmsHistory(token, { ...filters, page, per_page: perPage });
      setRows(result.data);
      setMeta(result.meta);
      if (result.types.length) setTypes(result.types);
    } catch (err) {
      setError(err.message || "Erreur lors du chargement de l'historique des SMS");
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILTRES
  // ============================================
  const clientName = useMemo(() => {
    const map = {};
    clients.forEach(c => { map[String(c.business_id)] = c.business_name; });
    return map;
  }, [clients]);

  const activeFilters = useMemo(() => {
    const list = [];
    if (filters.business_id) list.push({ key: 'business_id', label: `Client : ${clientName[filters.business_id] || filters.business_id}` });
    if (filters.date_from) list.push({ key: 'date_from', label: `Du ${filters.date_from}` });
    if (filters.date_to) list.push({ key: 'date_to', label: `Au ${filters.date_to}` });
    if (filters.type_sms) list.push({ key: 'type_sms', label: `Type : ${filters.type_sms}` });
    if (filters.num_en_ref) list.push({ key: 'num_en_ref', label: `N° réf. : ${filters.num_en_ref}` });
    return list;
  }, [filters, clientName]);

  const draftDateError = draft.date_from && draft.date_to && draft.date_to < draft.date_from
    ? 'La date de fin doit être postérieure ou égale à la date de début'
    : '';

  const openFilterModal = () => {
    setDraft(filters);
    setShowFilterModal(true);
  };

  const applyFilters = () => {
    if (draftDateError) return;
    const trimmed = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, String(v ?? '').trim()]));
    setFilters(trimmed);
    setPage(1);
    setShowFilterModal(false);
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setShowFilterModal(false);
  };

  const removeFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
    setPage(1);
  };

  const handleRefresh = async () => {
    await loadSms();
    showNotification('Historique actualisé', 'success', 3000);
  };

  const firstRow = meta.total === 0 ? 0 : (meta.current_page - 1) * meta.per_page + 1;
  const lastRow = Math.min(meta.current_page * meta.per_page, meta.total);

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="gsm-page main main-app p-3 p-lg-4">
      <GsmStyles />
      <GsmToasts notifications={notifications} onClose={hideNotification} />

      {/* ========== HEADER ========== */}
      <div className="gsm-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-header-icon">
              <i className="ri-message-3-line"></i>
            </div>
            <div>
              <h3 className="gsm-header-title mb-1">Historique SMS</h3>
              <p className="gsm-header-subtitle mb-0">SMS reçus et traités par les passerelles GSM</p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="gsm-btn-secondary" onClick={handleRefresh} disabled={loading}>
              <i className={`bi ${loading ? 'bi-arrow-repeat gsm-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
            <button className="gsm-btn-primary" onClick={openFilterModal}>
              <i className="bi bi-funnel"></i>
              <span>Filtrer{activeFilters.length > 0 ? ` (${activeFilters.length})` : ''}</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="gsm-alert gsm-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <button className="gsm-btn-secondary" onClick={loadSms}>Réessayer</button>
        </div>
      )}

      {/* ========== FILTRES ACTIFS ========== */}
      <div className="gsm-filters mb-4">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="text-muted small fw-semibold me-1">
            {meta.total.toLocaleString('fr-FR')} SMS
            {activeFilters.length > 0 ? ' correspondant aux filtres' : ''}
          </span>
          {activeFilters.map(f => (
            <span key={f.key} className="gsm-badge gsm-badge-gray">
              {f.label}
              <i className="bi bi-x-lg" role="button" title="Retirer ce filtre" onClick={() => removeFilter(f.key)}></i>
            </span>
          ))}
          {activeFilters.length > 0 && (
            <button className="btn btn-link btn-sm p-0 ms-1" onClick={resetFilters}>Réinitialiser</button>
          )}
          <div className="ms-auto d-flex align-items-center gap-2">
            <span className="text-muted small">Par page</span>
            <select
              className="gsm-select"
              style={{ width: 90, padding: '6px 10px' }}
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            >
              {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ========== TABLE ========== */}
      <div className="gsm-table-card mb-4">
        {loading && rows.length === 0 ? (
          <div className="gsm-empty"><Spinner animation="border" /><div className="mt-2">Chargement des SMS...</div></div>
        ) : rows.length === 0 ? (
          <div className="gsm-empty">
            <i className="ri-message-3-line"></i>
            {activeFilters.length > 0 ? 'Aucun SMS ne correspond aux filtres' : 'Aucun SMS enregistré'}
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ opacity: loading ? 0.5 : 1 }}>
              <Table className="gsm-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Type</th>
                    <th>N° réf.</th>
                    <th>Expéditeur</th>
                    <th>Montant</th>
                    <th>Trans ID</th>
                    <th>Statut API</th>
                    <th className="text-end">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(sms => (
                    <tr key={sms.id}>
                      <td className="text-nowrap">{formatDateTime(sms.reg_date)}</td>
                      <td>
                        {sms.business_id ? (
                          <>
                            <div className="fw-semibold">{sms.business_name || clientName[String(sms.business_id)] || '—'}</div>
                            <small className="gsm-mono text-muted">{sms.business_id}</small>
                          </>
                        ) : <span className="text-muted">Non affecté</span>}
                      </td>
                      <td>
                        {sms.type_sms
                          ? <span className={`gsm-badge ${TYPE_BADGE[sms.type_sms] || 'gsm-badge-gray'}`}>{sms.type_sms}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td><span className="gsm-mono">{sms.num_en_ref || '—'}</span></td>
                      <td><span className="gsm-mono">{sms.sender_sms || '—'}</span></td>
                      <td className="text-nowrap">{formatAmount(sms.montant_paye)}</td>
                      <td>{sms.bank_trans_id ? <span className="gsm-code">{sms.bank_trans_id}</span> : <span className="text-muted">—</span>}</td>
                      <td>{statusBadge(sms.statut_api)}</td>
                      <td className="text-end">
                        <button className="gsm-action-btn gsm-action-edit" title="Voir le SMS" onClick={() => setSelectedSms(sms)}>
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 border-top">
              <small className="text-muted">
                {firstRow}–{lastRow} sur {meta.total.toLocaleString('fr-FR')}
              </small>
              <PaginationControl
                currentPage={meta.current_page}
                totalPages={meta.last_page}
                onPageChange={(p) => { if (p >= 1 && p <= meta.last_page) setPage(p); }}
              />
            </div>
          </>
        )}
      </div>

      {/* ========== MODAL FILTRES ========== */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} size="lg" centered className="gsm-modal">
        <div className="gsm-modal-header gsm-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-modal-icon"><i className="bi bi-funnel"></i></div>
            <div>
              <h5 className="gsm-modal-title">Filtrer les SMS</h5>
              <small className="gsm-modal-subtitle">Les critères renseignés sont cumulés</small>
            </div>
          </div>
          <button className="gsm-modal-close" onClick={() => setShowFilterModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="gsm-modal-body">
          <Row className="g-3">
            <Col md={6}>
              <label className="gsm-form-label">Client (business_id)</label>
              <select
                className="gsm-select"
                value={draft.business_id}
                onChange={(e) => setDraft({ ...draft, business_id: e.target.value })}
              >
                <option value="">Tous les clients</option>
                {clients.map(c => (
                  <option key={c.business_id} value={c.business_id}>
                    {c.business_name} ({c.business_id})
                  </option>
                ))}
              </select>
            </Col>
            <Col md={6}>
              <label className="gsm-form-label">Type de SMS</label>
              <select
                className="gsm-select"
                value={draft.type_sms}
                onChange={(e) => setDraft({ ...draft, type_sms: e.target.value })}
              >
                <option value="">Tous les types</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Col>
            <Col md={6}>
              <label className="gsm-form-label">Date de début</label>
              <input
                type="date"
                className="gsm-input"
                value={draft.date_from}
                onChange={(e) => setDraft({ ...draft, date_from: e.target.value })}
              />
            </Col>
            <Col md={6}>
              <label className="gsm-form-label">Date de fin</label>
              <input
                type="date"
                className={`gsm-input ${draftDateError ? 'is-invalid' : ''}`}
                value={draft.date_to}
                min={draft.date_from || undefined}
                onChange={(e) => setDraft({ ...draft, date_to: e.target.value })}
              />
              {draftDateError && <div className="gsm-form-error">{draftDateError}</div>}
            </Col>
            <Col md={12}>
              <label className="gsm-form-label">Numéro de référence (num_en_ref)</label>
              <input
                className="gsm-input"
                placeholder="Ex. 077875666 (recherche partielle)"
                maxLength={30}
                value={draft.num_en_ref}
                onChange={(e) => setDraft({ ...draft, num_en_ref: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              />
              <div className="gsm-form-hint"><i className="bi bi-info-circle"></i>La date porte sur la date d'enregistrement du SMS</div>
            </Col>
          </Row>
        </Modal.Body>

        <div className="gsm-modal-footer">
          <button className="gsm-btn-cancel me-auto" onClick={resetFilters}>
            <i className="bi bi-x-circle"></i> Réinitialiser
          </button>
          <button className="gsm-btn-cancel" onClick={() => setShowFilterModal(false)}>Annuler</button>
          <button className="gsm-btn-primary" onClick={applyFilters} disabled={!!draftDateError}>
            <i className="bi bi-check-lg"></i> Appliquer
          </button>
        </div>
      </Modal>

      {/* ========== MODAL DÉTAIL SMS ========== */}
      <Modal show={!!selectedSms} onHide={() => setSelectedSms(null)} size="lg" centered className="gsm-modal">
        {selectedSms && (
          <>
            <div className="gsm-modal-header gsm-modal-header-blue">
              <div className="d-flex align-items-center gap-3">
                <div className="gsm-modal-icon"><i className="ri-message-3-line"></i></div>
                <div>
                  <h5 className="gsm-modal-title">SMS #{selectedSms.id}</h5>
                  <small className="gsm-modal-subtitle">{formatDateTime(selectedSms.reg_date)}</small>
                </div>
              </div>
              <button className="gsm-modal-close" onClick={() => setSelectedSms(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <Modal.Body className="gsm-modal-body">
              <div className="gsm-alert gsm-alert-info mb-3" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {selectedSms.sms_gsm || '—'}
              </div>
              <Row className="g-3">
                {[
                  ['Client', selectedSms.business_name ? `${selectedSms.business_name} (${selectedSms.business_id})` : (selectedSms.business_id || 'Non affecté')],
                  ['Type', selectedSms.type_sms || '—'],
                  ['N° réf.', selectedSms.num_en_ref || '—'],
                  ['Expéditeur', selectedSms.sender_sms || '—'],
                  ['Montant', formatAmount(selectedSms.montant_paye)],
                  ['Solde', formatAmount(selectedSms.solde)],
                  ['Trans ID', selectedSms.bank_trans_id || '—'],
                  ['Statut API', selectedSms.statut_api ?? '—'],
                  ['Passerelle / port', `${selectedSms.device_sn || '—'} / ${selectedSms.port || '—'}`],
                  ['Date du SMS', formatDateTime(selectedSms.sms_timestamp)]
                ].map(([label, value]) => (
                  <Col md={6} key={label}>
                    <div className="gsm-form-label mb-1">{label}</div>
                    <div className="gsm-mono">{value}</div>
                  </Col>
                ))}
              </Row>
            </Modal.Body>
            <div className="gsm-modal-footer">
              <button className="gsm-btn-cancel" onClick={() => setSelectedSms(null)}>Fermer</button>
            </div>
          </>
        )}
      </Modal>

      <Footer />
    </div>
  );
}
